// src/pages/ChatBot/evals/api.ts
//
// Thin client for the evaluation admin API (mounted at /chatbot/evals). Auth
// rides on cookies like the rest of the console — every call sends
// credentials: 'include'. Errors surface as EvalApiError carrying the server's
// { error } message and HTTP status, so callers can branch on 409 (a run is
// already in progress) vs 422 (the golden set is empty). Mirrors the
// query-logs/api.ts idiom.

import type { EvalRunResponse, EvalRunsResponse, StartEvalRunResponse } from './types';

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/chatbot/evals`;

export class EvalApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'EvalApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: init?.body
      ? { 'Content-Type': 'application/json', ...init.headers }
      : init?.headers,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') message = body.error;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new EvalApiError(message, res.status);
  }
  return res.json() as Promise<T>;
}

// Starts an evaluation over the golden set. 202 → { runId }; the server rejects
// with 409 (a run is already in progress) or 422 (the golden set is empty),
// both of which arrive as an EvalApiError the caller branches on by status.
export function startEvalRun(): Promise<StartEvalRunResponse> {
  return request<StartEvalRunResponse>('/run', { method: 'POST' });
}

// Lists recent runs (newest first, max 20) — the compact projection.
export function fetchEvalRuns(): Promise<EvalRunsResponse> {
  return request<EvalRunsResponse>('/runs');
}

// Fetches one run's full document (config, metrics, per-query results).
export function fetchEvalRun(id: string): Promise<EvalRunResponse> {
  return request<EvalRunResponse>(`/runs/${encodeURIComponent(id)}`);
}
